/**
 * Simplified Admin Redux Slice
 * Basic admin state management
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { SystemHealth, SystemStats, adminApi } from '../services/adminApi';

// Async thunks for API calls
export const fetchSystemHealth = createAsyncThunk(
  'admin/fetchSystemHealth',
  async () => {
    return await adminApi.getSystemHealth();
  }
);

export const fetchSystemStats = createAsyncThunk(
  'admin/fetchSystemStats', 
  async () => {
    return await adminApi.getSystemStats();
  }
);

export const fetchDashboardSummary = createAsyncThunk(
  'admin/fetchDashboardSummary',
  async () => {
    return await adminApi.getDashboardSummary();
  }
);

// Admin state interface
interface AdminState {
  // System monitoring
  systemHealth: SystemHealth | null;
  systemStats: SystemStats | null;
  healthLastUpdated: string | null;
  
  // Placeholder for future features
  activeBulkOperations: any[];
  
  // UI state
  loading: {
    health: boolean;
    stats: boolean;
    dashboard: boolean;
  };
  
  error: string | null;
}

// Initial state
const initialState: AdminState = {
  systemHealth: null,
  systemStats: null,
  healthLastUpdated: null,
  
  activeBulkOperations: [],
  
  loading: {
    health: false,
    stats: false,
    dashboard: false,
  },
  
  error: null,
};

// Admin slice
const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    // Error handling
    clearError: (state) => {
      state.error = null;
    },
    
    // Real-time updates
    updateSystemHealth: (state, action) => {
      state.systemHealth = action.payload;
      state.healthLastUpdated = new Date().toISOString();
    },
  },
  
  extraReducers: (builder) => {
    // System Health
    builder
      .addCase(fetchSystemHealth.pending, (state) => {
        state.loading.health = true;
      })
      .addCase(fetchSystemHealth.fulfilled, (state, action) => {
        state.loading.health = false;
        state.systemHealth = action.payload;
        state.healthLastUpdated = new Date().toISOString();
      })
      .addCase(fetchSystemHealth.rejected, (state, action) => {
        state.loading.health = false;
        state.error = action.error.message || 'Failed to fetch system health';
      });
    
    // System Stats
    builder
      .addCase(fetchSystemStats.pending, (state) => {
        state.loading.stats = true;
      })
      .addCase(fetchSystemStats.fulfilled, (state, action) => {
        state.loading.stats = false;
        state.systemStats = action.payload;
      })
      .addCase(fetchSystemStats.rejected, (state, action) => {
        state.loading.stats = false;
        state.error = action.error.message || 'Failed to fetch system stats';
      });
    
    // Dashboard Summary
    builder
      .addCase(fetchDashboardSummary.pending, (state) => {
        state.loading.dashboard = true;
      })
      .addCase(fetchDashboardSummary.fulfilled, (state, action) => {
        state.loading.dashboard = false;
        const { systemHealth, systemStats, recentAlerts, pendingOperations } = action.payload;
        state.systemHealth = systemHealth;
        state.systemStats = systemStats;
        state.healthLastUpdated = new Date().toISOString();
        state.activeBulkOperations = pendingOperations;
      })
      .addCase(fetchDashboardSummary.rejected, (state, action) => {
        state.loading.dashboard = false;
        state.error = action.error.message || 'Failed to fetch dashboard data';
      });
  },
});

// Export actions and reducer
export const { clearError, updateSystemHealth } = adminSlice.actions;
export default adminSlice.reducer;